/**
 * TreatmentGuide.jsx
 *
 * Read-only reference rail shown beside the submission wizard (Step 2). Mirrors
 * the printed "AE Codes" reference sheet for the selected treatment so the
 * reporter can identify which code / category applies. Content comes from
 * lib/treatmentGuides.js — pass the treatment value + the currently-selected
 * severity code to highlight the matching card and move it to the top of the list.
 *
 * Layout-agnostic: the parent decides where it sits (currently a sticky right
 * rail). Renders nothing when the treatment has no guide.
 */

import React, { useEffect, useRef, useMemo } from 'react';
import { getTreatmentGuide } from '../lib/treatmentGuides.js';

export default function TreatmentGuide({ treatment, activeCode, variant = 'rail' }) {
  const guide = getTreatmentGuide(treatment);
  const asideRef = useRef(null);
  const isDrawer = variant === 'drawer';

  const orderedCodes = useMemo(() => {
    if (!guide?.codes) return [];
    if (!activeCode) return guide.codes;
    const picked = guide.codes.find(c => c.code === activeCode);
    if (!picked) return guide.codes;
    return [picked, ...guide.codes.filter(c => c.code !== activeCode)];
  }, [guide, activeCode]);

  // Selected card moves to the top — reset scroll so it is immediately visible.
  useEffect(() => {
    asideRef.current?.scrollTo({ top: 0, behavior: 'smooth' });
  }, [activeCode, treatment]);

  if (!guide) return null;

  return (
    // Deliberately NOT an <aside>: global.css owns the bare aside element for the
    // app sidebar (incl. an off-canvas translateX(-100%) rule under 820px).
    <section
      ref={asideRef}
      role="complementary"
      aria-label={`${guide.title} reference`}
      style={{
        ...(isDrawer
          ? { flex: 1, minHeight: 0, height: '100%', maxHeight: '100%' }
          : { height: '100%', maxHeight: '100%' }),
        overflowY: 'auto',
        border: '1px solid var(--border)',
        borderRadius: 12,
        background: 'var(--surface)',
        padding: 16,
        boxSizing: 'border-box',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, marginBottom: 6 }}>
        <h2 style={{ fontSize: 15, fontWeight: 700, margin: 0 }}>{guide.title}</h2>
        <span style={{ fontSize: 11, color: 'var(--text-3)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '.04em' }}>
          Reference
        </span>
      </div>
      <p style={{ fontSize: 12, lineHeight: 1.5, color: 'var(--text-3)', margin: '0 0 14px' }}>{guide.intro}</p>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        {orderedCodes.map(c => {
          const active = c.code === activeCode;
          return (
            <div
              key={c.code}
              style={{
                border: '1px solid',
                borderColor: active ? 'var(--brand)' : 'var(--border)',
                borderRadius: 10,
                overflow: 'hidden',
                background: active ? 'var(--brand-weak)' : 'var(--surface-2)',
                boxShadow: active ? '0 0 0 3px rgba(99,102,241,.10)' : 'none',
                opacity: active || !activeCode ? 1 : 0.72,
                transition: 'border-color .15s, background .15s, opacity .15s, box-shadow .15s',
              }}
            >
              {/* Header */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '9px 12px' }}>
                <span
                  style={{
                    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                    minWidth: 22, height: 22, padding: '0 6px', borderRadius: 6,
                    fontSize: 11, fontWeight: 700,
                    background: active ? 'var(--brand)' : 'var(--border)',
                    color: active ? '#fff' : 'var(--text-2)',
                  }}
                >
                  {c.code.replace(/^Code\s*/i, '')}
                </span>
                <span style={{ fontSize: 13, fontWeight: 700, color: 'var(--text-1, var(--text-2))' }}>{c.label}</span>
              </div>

              {/* Example image */}
              {c.image && (
                <img
                  src={c.image}
                  alt={`Example of ${c.label} (${c.code})`}
                  loading="lazy"
                  style={{
                    display: 'block', width: '100%', height: 'auto',
                    borderTop: '1px solid var(--border)', borderBottom: '1px solid var(--border)',
                  }}
                />
              )}

              {/* Body */}
              <div style={{ padding: '10px 12px' }}>
                <GuideList heading="What it is" items={c.whatItIs} />
                <GuideList heading="How it's caused" items={c.causes} muted />
              </div>
            </div>
          );
        })}
      </div>

      {/* Shared "how to proceed" — identical for every code */}
      {guide.proceed?.length > 0 && (
        <div style={{ marginTop: 12, padding: '10px 12px', border: '1px dashed var(--border)', borderRadius: 10 }}>
          <GuideList heading="How to proceed" items={guide.proceed} ordered />
        </div>
      )}
    </section>
  );
}

function GuideList({ heading, items, muted = false, ordered = false }) {
  if (!items?.length) return null;
  const ListTag = ordered ? 'ol' : 'ul';
  return (
    <div style={{ marginTop: 8 }}>
      <div style={{ fontSize: 10.5, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.06em', color: 'var(--text-3)', marginBottom: 4 }}>
        {heading}
      </div>
      <ListTag
        style={{
          margin: 0, paddingLeft: 16, fontSize: 12, lineHeight: 1.5,
          color: muted ? 'var(--text-3)' : 'var(--text-2)',
        }}
      >
        {items.map((it, i) => <li key={i} style={{ marginBottom: 1 }}>{it}</li>)}
      </ListTag>
    </div>
  );
}
